import React, { useState } from 'react';
import { X, Plane, Send, CheckCircle2, MapPin, Package, AlertCircle } from 'lucide-react';
import { db } from '../lib/firebase';
import { doc, setDoc } from 'firebase/firestore';

interface CourierQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialWeightKg?: number;
  initialState?: string;
  suiteNumber?: string;
  userName?: string;
  userEmail?: string;
}

export const CourierQuoteModal: React.FC<CourierQuoteModalProps> = ({
  isOpen,
  onClose,
  initialWeightKg = 1.5,
  initialState = 'SP',
  suiteNumber = 'DJ-SUITE',
  userName = '',
  userEmail = ''
}) => {
  const [cep, setCep] = useState('');
  const [cityState, setCityState] = useState(initialState ? `— / ${initialState}` : '');
  const [weightKg, setWeightKg] = useState(initialWeightKg);
  const [boxDimensions, setBoxDimensions] = useState('');
  const [clientName, setClientName] = useState(userName);
  const [contactEmail, setContactEmail] = useState(userEmail);
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 8) raw = raw.slice(0, 8);
    if (raw.length > 5) {
      setCep(`${raw.slice(0, 5)}-${raw.slice(5)}`);
    } else {
      setCep(raw);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cep.replace(/\D/g, '').length < 8) {
      setError('Por favor digite um CEP válido com 8 dígitos para cotação com a transportadora.');
      return;
    }

    setLoading(true);
    setError(null);

    const quoteId = `quote-${Date.now()}`;
    const payload = {
      id: quoteId,
      cep,
      cityState,
      weightKg,
      clientName,
      contactEmail,
      notes,
      suiteNumber,
      createdAt: new Date().toISOString(),
      status: 'pendente'
    };

    try {
      await setDoc(doc(db, 'courier_quotes', quoteId), payload);
    } catch (err) {
      console.warn('Saved courier quote request in Firestore note:', err);
    }

    setLoading(false);
    setSubmitted(true);
  };

  const handleReset = () => {
    setSubmitted(false);
    setCep('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl overflow-hidden border border-slate-100 my-8">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-700 via-indigo-700 to-blue-800 text-white p-5 sm:p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/10 rounded-2xl border border-white/20">
              <Plane className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <h3 className="text-lg font-black">Orçamento Frete Courrier</h3>
              <p className="text-indigo-100 text-xs">FedEx, UPS & DHL Express (Japão → Brasil)</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {submitted ? (
          <div className="p-6 sm:p-8 text-center space-y-4">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <div className="space-y-1">
              <h4 className="text-xl font-black text-slate-800">Solicitação de Cotação Enviada!</h4>
              <p className="text-xs sm:text-sm text-slate-600 max-w-sm mx-auto">
                Recebemos seu pedido para o CEP <strong className="font-mono text-slate-900">{cep}</strong> ({weightKg.toFixed(2)} kg).
              </p>
            </div>
            <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl text-xs text-indigo-950 font-medium text-left space-y-1.5">
              <p className="font-bold flex items-center gap-1.5 text-indigo-900">
                <Package className="w-4 h-4 text-indigo-600" />
                O que acontece agora?
              </p>
              <p>• Nossa equipe no Japão consultará a melhor tarifa promocional corporativa junto às transportadoras (FedEx, UPS e DHL).</p>
              <p>• Você receberá a proposta comparativa de valores e prazos na sua Suíte <strong className="font-mono">{suiteNumber}</strong> e e-mail em até 24 horas úteis.</p>
            </div>
            <button
              onClick={handleReset}
              className="w-full py-3 bg-[#1D3557] hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow-md transition-all"
            >
              Concluir e Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-xl text-xs border border-red-200">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-900 flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-bold">Cotação Sob Consulta Privada</span>
                <p className="text-[11px] text-amber-800/90 mt-0.5">
                  Devido às variações de combustível e zonas tarifárias de entregas privadas no Brasil, precisamos do seu CEP exato para obter o menor preço promocional disponível.
                </p>
              </div>
            </div>

            {/* CEP Input */}
            <div>
              <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1">
                CEP de Destino no Brasil *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={cep}
                  onChange={handleCepChange}
                  placeholder="00000-000"
                  required
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 font-mono text-base font-black text-slate-900 focus:ring-2 focus:ring-indigo-600 focus:outline-none"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold">
                  BRASIL
                </span>
              </div>
            </div>

            {/* City / State & Weight */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Cidade / Estado</label>
                <input
                  type="text"
                  value={cityState}
                  onChange={(e) => setCityState(e.target.value)}
                  placeholder="Ex: São Paulo / SP"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Peso Estimado (kg) *</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={weightKg}
                  onChange={(e) => setWeightKg(parseFloat(e.target.value) || 0.1)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-extrabold font-mono"
                  required
                />
              </div>
            </div>

            {/* Client Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Seu Nome / Suíte</label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Nome do Cliente"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">E-mail para Retorno</label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold"
                />
              </div>
            </div>

            {/* Optional Dimensions or Notes */}
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">
                Dimensões Aprox. CxLxA (cm) ou Observações <span className="font-normal text-slate-400">(Opcional)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Ex: Caixa 30x20x15cm com eletrônicos e brinquedos"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium"
              />
            </div>

            {/* Footer Buttons */}
            <div className="pt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-700 to-indigo-700 hover:from-purple-800 hover:to-indigo-800 text-white font-extrabold text-xs rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Solicitar Orçamento do CEP</span>
                    <Send className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          </form>
        )}

      </div>
    </div>
  );
};

export default CourierQuoteModal;
